import {
  GroupDataMessage,
  OnConnectedArgs,
  OnDisconnectedArgs,
  OnGroupDataMessageArgs,
  OnRejoinGroupFailedArgs,
  OnServerDataMessageArgs,
  OnStoppedArgs,
  ServerDataMessage,
  WebPubSubClient,
} from '@azure/web-pubsub-client'

import { joinGroups, leaveGroups, negotiateConnection } from './services'
import {
  ConnectionStatus,
  EventCallback,
  JoinGroupsRequest,
  LeaveGroupsRequest,
  NegotiateItem,
  NegotiateResponse,
  NotificationData,
  NotificationSignal,
  NotificationSignalType,
} from './types'

class WebPubSubClientManager {
  private static instance: WebPubSubClientManager | null = null

  private client: WebPubSubClient | null = null
  private isConnecting = false
  private isLoggingOut = false

  private connectionStatus: ConnectionStatus = ConnectionStatus.idle
  private connectionError: string | undefined
  private activeGroups: string[] = []

  private readonly connectedCallbacks = new Set<EventCallback>()
  private readonly disconnectedCallbacks = new Set<EventCallback>()
  private readonly stoppedCallbacks = new Set<EventCallback>()
  private readonly serverMessageCallbacks = new Set<EventCallback>()
  private readonly groupMessageCallbacks = new Set<EventCallback>()
  private readonly rejoinGroupFailedCallbacks = new Set<EventCallback>()
  private readonly stateChangeCallbacks = new Set<
    (s: { status: ConnectionStatus; error?: string; groups: string[] }) => void
  >()

  private constructor() {}

  static getInstance(): WebPubSubClientManager {
    WebPubSubClientManager.instance ??= new WebPubSubClientManager()
    return WebPubSubClientManager.instance
  }

  getClient(): WebPubSubClient | null {
    return this.client
  }

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus
  }

  getError(): string | undefined {
    return this.connectionError
  }

  getActiveGroups(): string[] {
    return [...this.activeGroups]
  }

  onStateChange(
    callback: (s: {
      status: ConnectionStatus
      error?: string
      groups: string[]
    }) => void
  ): () => void {
    this.stateChangeCallbacks.add(callback)
    return () => this.stateChangeCallbacks.delete(callback)
  }

  onConnected(callback: EventCallback): () => void {
    this.connectedCallbacks.add(callback)
    return () => this.connectedCallbacks.delete(callback)
  }

  onDisconnected(callback: EventCallback): () => void {
    this.disconnectedCallbacks.add(callback)
    return () => this.disconnectedCallbacks.delete(callback)
  }

  onStopped(callback: EventCallback): () => void {
    this.stoppedCallbacks.add(callback)
    return () => this.stoppedCallbacks.delete(callback)
  }

  onServerMessage(callback: EventCallback): () => void {
    this.serverMessageCallbacks.add(callback)
    return () => this.serverMessageCallbacks.delete(callback)
  }

  onGroupMessage(callback: EventCallback): () => void {
    this.groupMessageCallbacks.add(callback)
    return () => this.groupMessageCallbacks.delete(callback)
  }

  onRejoinGroupFailed(callback: EventCallback): () => void {
    this.rejoinGroupFailedCallbacks.add(callback)
    return () => this.rejoinGroupFailedCallbacks.delete(callback)
  }

  async connect(negotiateResponse?: NegotiateResponse): Promise<void> {
    if (this.isConnecting) return
    if (
      this.connectionStatus === ConnectionStatus.connected ||
      this.connectionStatus === ConnectionStatus.connecting
    )
      return

    this.isConnecting = true

    try {
      this.setConnectionError(undefined)

      let response: NegotiateResponse

      if (negotiateResponse) {
        response = negotiateResponse
      } else {
        this.setConnectionStatus(ConnectionStatus.negotiating)
        response = await negotiateConnection()
      }

      if (!response?.success || !response.data?.[0]?.url) {
        throw new Error('Negotiate failed')
      }

      const item: NegotiateItem = response.data[0]

      await this.teardownClient()

      this.setConnectionStatus(ConnectionStatus.connecting)

      const client = new WebPubSubClient(item.url, {
        autoReconnect: true,
        autoRejoinGroups: true,
      })

      this.client = client
      this.setupEventHandlers(client)

      await client.start()
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Connection failed'
      this.setConnectionStatus(ConnectionStatus.error)
      this.setConnectionError(errorMessage)
      throw error
    } finally {
      this.isConnecting = false
    }
  }

  async disconnect(isLogout = false): Promise<void> {
    if (isLogout) {
      this.isLoggingOut = true
    }
    await this.teardownClient()
    this.setConnectionError(undefined)
    this.setConnectionStatus(ConnectionStatus.stopped)
  }

  reset(): void {
    this.client = null
    this.isConnecting = false
    this.isLoggingOut = false
    this.connectionStatus = ConnectionStatus.idle
    this.connectionError = undefined
    this.activeGroups = []
    this.emitState()
  }

  isUserLoggingOut(): boolean {
    return this.isLoggingOut
  }

  async joinGroups(request: JoinGroupsRequest): Promise<void> {
    const items = request.group ?? []

    if (this.connectionStatus !== ConnectionStatus.connected) {
      throw new Error(
        `Cannot join groups - WebSocket not connected (status: ${this.connectionStatus})`
      )
    }

    await joinGroups(request)
    items.forEach((g) => this.addGroup(`${g.group_type}:${g.group_id}`))
  }

  async leaveGroups(request: LeaveGroupsRequest): Promise<void> {
    const names = request.group_names ?? []

    if (this.connectionStatus !== ConnectionStatus.connected) {
      throw new Error(
        `Cannot leave groups - WebSocket not connected (status: ${this.connectionStatus})`
      )
    }

    await leaveGroups(request)
    names.forEach((n) => this.removeGroup(n))
  }

  private async teardownClient(): Promise<void> {
    const client = this.client
    this.client = null
    if (!client) return
    try {
      client.stop()
    } catch {
      return
    }
  }

  private setupEventHandlers(client: WebPubSubClient): void {
    client.on('connected', (e: OnConnectedArgs) => {
      this.setConnectionStatus(ConnectionStatus.connected)
      const signal: NotificationSignal = {
        type: NotificationSignalType.Connected,
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        connectionId: e.connectionId,
        userId: e.userId,
        rawEvent: e,
      }
      this.dispatchEvent(this.connectedCallbacks, signal)
    })

    client.on('disconnected', (e: OnDisconnectedArgs) => {
      this.setConnectionStatus(ConnectionStatus.disconnected)
      const signal: NotificationSignal = {
        type: NotificationSignalType.Disconnected,
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        rawEvent: e,
      }
      this.dispatchEvent(this.disconnectedCallbacks, signal)
    })

    client.on('stopped', (e: OnStoppedArgs) => {
      this.setConnectionStatus(ConnectionStatus.stopped)
      const signal: NotificationSignal = {
        type: NotificationSignalType.Stopped,
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        rawEvent: e,
      }
      this.dispatchEvent(this.stoppedCallbacks, signal)
    })

    client.on('group-message', (e: OnGroupDataMessageArgs) => {
      const msg: GroupDataMessage = e.message

      const signal: NotificationSignal = {
        type: NotificationSignalType.GroupMessage,
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        group: msg.group,
        dataType: msg.dataType,
        data: msg.data,
        fromUserId: msg.fromUserId,
        sequenceId: msg.sequenceId,
        rawEvent: e,
      }
      this.dispatchEvent(this.groupMessageCallbacks, signal)
    })

    client.on('server-message', (e: OnServerDataMessageArgs) => {
      const msg: ServerDataMessage = e.message

      const signal: NotificationSignal = {
        type: NotificationSignalType.ServerMessage,
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        dataType: msg.dataType,
        data: msg.data as NotificationData,
        sequenceId: msg.sequenceId,
        rawEvent: e,
      }
      this.dispatchEvent(this.serverMessageCallbacks, signal)
    })

    client.on('rejoin-group-failed', (e: OnRejoinGroupFailedArgs) => {
      this.setConnectionStatus(ConnectionStatus.error)
      const signal: NotificationSignal = {
        type: NotificationSignalType.RejoinGroupFailed,
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        group: (e as { group?: string }).group,
        rawEvent: e,
      }
      this.dispatchEvent(this.rejoinGroupFailedCallbacks, signal)
    })
  }

  private setConnectionStatus(status: ConnectionStatus): void {
    if (this.connectionStatus === status) return
    this.connectionStatus = status
    this.emitState()
  }

  private setConnectionError(error: string | undefined): void {
    if (this.connectionError === error) return
    this.connectionError = error
    this.emitState()
  }

  private addGroup(groupName: string): void {
    if (this.activeGroups.includes(groupName)) return
    this.activeGroups = [...this.activeGroups, groupName]
    this.emitState()
  }

  private removeGroup(groupName: string): void {
    const next = this.activeGroups.filter((g) => g !== groupName)
    if (next.length === this.activeGroups.length) return
    this.activeGroups = next
    this.emitState()
  }

  private emitState(): void {
    const snapshot = {
      status: this.connectionStatus,
      error: this.connectionError,
      groups: [...this.activeGroups],
    }
    this.stateChangeCallbacks.forEach((cb) => {
      try {
        cb(snapshot)
      } catch {
        return
      }
    })
  }

  private dispatchEvent(
    callbacks: Set<EventCallback>,
    signal: NotificationSignal
  ): void {
    callbacks.forEach((callback) => {
      try {
        callback(signal)
      } catch {
        return
      }
    })
  }

  private generateId(): string {
    return `${Date.now()}-${crypto.randomUUID()}`
  }
}

export { WebPubSubClientManager }
